import { inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { map } from 'rxjs/operators';
import { UTC_DATE_FIELDS, DateTimeService } from '@application/data-access';
import { DateTime } from 'luxon';
import { cleanDateStr } from '@shared/utils';

/**
 * HTTP Interceptor that automatically converts date fields between UTC and local timezone.
 *
 * - Outgoing requests: Converts all fields ending with "Utc" from local → UTC,
 *   in the body AND in the query parameters
 * - Incoming responses: Converts all fields ending with "Utc" from UTC → local
 *
 * Handles nested objects and arrays recursively.
 *
 * Um pedido pode declarar exceções — atributos que são instantes apesar de o
 * nome não terminar em "Utc" — pelo token {@link UTC_DATE_FIELDS}, e nesse caso
 * entram no mesmo caminho de todos os outros:
 *
 * ```ts
 * this.http.get<T>(url, { context: withUtcDateFields(['SendAt']) });
 * ```
 */
export const DateInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): ReturnType<HttpHandlerFn> => {
  const dateTimeService = inject(DateTimeService);
  const dateFields = request.context.get(UTC_DATE_FIELDS);

  const filter = request.params.get('$filter');
  const apply = request.params.get('$apply');

  let params = request.params;
  const userZone = dateTimeService.getUserZone();
  if (filter) params = params.set('$filter', adjustODataFilterDatesToUtc(filter, userZone, dateFields));
  if (apply) params = params.set('$apply', adjustODataFilterDatesToUtc(apply, userZone, dateFields));
  params = adjustParamDatesToUtc(params, dateTimeService, dateFields);

  if (
    request.body instanceof FormData ||
    request.body instanceof Blob ||
    request.body instanceof ArrayBuffer
  ) {
    return next(request.clone({ params }));
  }

  let convertedBody = request.body;
  if (convertedBody && typeof convertedBody === 'object') {
    convertedBody = dateTimeService.toUtcObject(convertedBody, dateFields);
  }

  const clonedRequest = request.clone({
    params,
    body: convertedBody,
  });

  return next(clonedRequest).pipe(
    map((event: HttpEvent<any>) => {
      if (event instanceof HttpResponse && event.body) {
        const convertedBody = dateTimeService.toLocalObject(
          event.body,
          dateFields,
        );
        return event.clone({
          body: convertedBody,
        });
      }
      
      return event;
    }),
  );
};

/**
 * Reescreve para UTC os parâmetros de query que são datas — os que terminam em
 * "Utc" e as exceções declaradas pelo pedido em `UTC_DATE_FIELDS`.
 *
 * Sem isto, um filtro solto (`?FromDateUtc=...`) saía em hora de parede da conta
 * rotulado como UTC e deslocava a janela pelo offset — com 200 OK e números
 * plausíveis. Era por isso que alguns serviços chamavam o conversor à mão antes
 * de montar a query; agora não precisam.
 *
 * Os parâmetros de sistema do OData (`$filter`, `$apply`, `$orderby`) ficam de
 * fora: não são valores de data, são expressões, e os seus literais têm o
 * caminho próprio em {@link adjustODataFilterDatesToUtc}.
 *
 * A conversão passa por `toUtcObject` com um objeto de uma chave só, de
 * propósito: a regra de o que é data e como se converte mora num sítio só.
 */
const adjustParamDatesToUtc = (
  params: HttpParams,
  dateTimeService: DateTimeService,
  dateFields: readonly string[],
): HttpParams => {
  const extra = new Set(dateFields.map((field) => field.toLowerCase()));
  const isDateParam = (key: string): boolean =>
    !key.startsWith('$') &&
    (key.endsWith('Utc') || extra.has(key.toLowerCase()));

  let result = params;

  for (const key of params.keys()) {
    if (!isDateParam(key)) continue;

    const values = params.getAll(key) ?? [];
    if (!values.length) continue;

    const converted = values.map((value) => {
      const utc = dateTimeService.toUtcObject(
        { [key]: value } as Record<string, string>,
        dateFields,
      );
      return utc[key] ?? value;
    });

    result = result.delete(key);
    for (const value of converted) {
      result = result.append(key, value);
    }
  }

  return result;
};

/**
 * Reescreve para UTC os literais de data das comparações cujo ATRIBUTO é um
 * instante — o que o servidor lê como tal. As outras (`DueOn`, `LastLoggedOn`,
 * ...) passam intactas, em hora de parede da conta.
 *
 * São instantes os atributos cujo nome contém "UTC" (a convenção) e os que o
 * pedido declara em `UTC_DATE_FIELDS` — `SendAt`, `SubscribedOn`, ... Sem a
 * segunda metade, um campo declarado saía convertido no CORPO e cru no
 * `$filter`, e as duas metades do pedido liam o mesmo atributo em fusos
 * diferentes: a janela deslocava-se pelo offset da conta e o pedido devolvia
 * 200 com menos linhas do que devia.
 *
 * `extraUtcDateFields` compara sem maiúsculas/minúsculas e aceita tanto o nome
 * simples como o último segmento de um caminho de navegação, portanto declarar
 * `'SendAt'` também apanha `Message/SendAt`. O chamador do `dxOdataStore`
 * omite-o: aquele caminho não passa pelo `HttpClient`, logo não tem contexto de
 * pedido onde declarar exceções.
 *
 * `userZone` é obrigatório de propósito: tem de vir de
 * `DateTimeService.getUserZone()`, que lê `ownerSettings()` — um `computed`, logo
 * síncrono e correto desde o primeiro pedido. NÃO usar `DateTime.now().zoneName`
 * aqui: esse é o `Settings.defaultZone`, que o `LUXON_ZONE_INITIALIZER` só
 * preenche a partir de um `effect()`, ou seja depois do primeiro flush de change
 * detection. Tudo o que saia antes disso — os pedidos do inicializador de idioma,
 * um resolver a disparar no mesmo tick em que `ownerSettings` chega — seria
 * convertido com o fuso da máquina, em silêncio.
 */
export const adjustODataFilterDatesToUtc = (
  filter: string,
  userZone: string,
  extraUtcDateFields: readonly string[] = []
): string => {
  const extra = new Set(
    extraUtcDateFields.map((field) => field.toLowerCase())
  );

  const isInstantAttribute = (attribute: string): boolean => {
    if (attribute.toUpperCase().includes('UTC')) {
      return true;
    }
    if (!extra.size) {
      return false;
    }
    const lower = attribute.toLowerCase();
    return extra.has(lower) || extra.has(lower.split('/').pop() ?? lower);
  };

  return filter.replace(
    /([\w/.]+)\s+(ge|le|gt|lt|eq|ne)\s+(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z)/g,
    (fullMatch, attribute, operator, isoDate) => {
      // Hora de parede (`DueOn`, `LastLoggedOn`, ...): passa intacta.
      if (!isInstantAttribute(attribute)) {
        return fullMatch;
      }

      try {
        const localDate = DateTime.fromISO(
          cleanDateStr(isoDate),
          { zone: userZone }
        );

        if (!localDate.isValid) {
          return fullMatch;
        }

        const utcDate = localDate.toUTC().toISO();

        return `${attribute} ${operator} ${utcDate}`;
      } catch {
        return fullMatch;
      }
    }
  );
}
